(function() {
  'use strict';

  const ACTIVITY_DEBOUNCE_MS = 5000;
  const RESUME_COUNTDOWN_IN_SECONDS = 10;
  const MOUSEMOVE_THROTTLE_MS = 1000;

  var body = document.body;
  var APP_PREFIX = (body && body.dataset.appName) || 'panacea';
  var STORAGE_EXPIRY_KEY = APP_PREFIX + '_session_expires_at';
  var STORAGE_TIMEOUT_KEY = APP_PREFIX + '_session_timeout_ms';

  var expiryTimer = null;
  var warningTimer = null;
  var countdownInterval = null;
  var isExtending = false;
  var activityDebounce = null;
  var lastServerPing = 0;
  var isPaused = false;
  var resumeTimer = null;
  var pausedRemainingMs = 0;
  var currentRemainingMs = 0;

  function isLoggedIn() {
    return !!document.querySelector('meta[name="session-expires-at"]');
  }

  function getSignInPath() {
    return (body && body.dataset.sessionSignInPath) || '/users/sign_in?session_expired=1';
  }

  /**
   * Read the server-configured session timeout in milliseconds.
   *
   * Priority:
   * 1. X-Session-Timeout-Seconds from last ping (cached in localStorage)
   * 2. Derive from session-expires-at meta tag: (expiresAt - Date.now())
   * 3. data-session-timeout-ms on <body>
   */
  function getTimeoutMs() {
    var stored = localStorage.getItem(STORAGE_TIMEOUT_KEY);
    if (stored) {
      var ms = parseInt(stored, 10);
      if (!isNaN(ms) && ms > 0) return ms;
    }

    var meta = document.querySelector('meta[name="session-expires-at"]');
    if (meta) {
      var val = parseInt(meta.getAttribute('content'), 10);
      if (!isNaN(val)) {
        var derived = (val * 1000) - Date.now();
        if (derived > 0) return derived;
      }
    }

    var bodyTimeout = body && body.dataset.sessionTimeoutMs;
    if (bodyTimeout) {
      var bodyMs = parseInt(bodyTimeout, 10);
      if (!isNaN(bodyMs) && bodyMs > 0) return bodyMs;
    }

    return 0;
  }

  /**
   * Compute the warning lead time. Never larger than half the total
   * timeout, so short timeouts (e.g. 1 min) still get a visible window.
   */
  function getWarningThresholdMs() {
    var timeoutMs = getTimeoutMs();
    if (!timeoutMs) return 0;
    return Math.min(60 * 1000, Math.floor(timeoutMs / 2));
  }

  /**
   * Return the most recent expiry timestamp we know about (ms since epoch).
   */
  function getExpiryTimestamp() {
    var metaExpiry = null;
    var meta = document.querySelector('meta[name="session-expires-at"]');
    if (meta) {
      var val = parseInt(meta.getAttribute('content'), 10);
      if (!isNaN(val)) metaExpiry = val * 1000;
    }

    var storedExpiry = localStorage.getItem(STORAGE_EXPIRY_KEY);
    var storageExpiry = null;
    if (storedExpiry) {
      var ms = parseInt(storedExpiry, 10);
      if (!isNaN(ms)) storageExpiry = ms;
    }

    if (metaExpiry && storageExpiry) {
      return Math.max(metaExpiry, storageExpiry);
    }
    return metaExpiry || storageExpiry || null;
  }

  /**
   * Persist the new expiry timestamp and optionally cache the timeout
   * duration from the X-Session-Timeout-Seconds header.
   */
  function setExpiryFromHeader(expiryHeader, timeoutHeader) {
    var meta = document.querySelector('meta[name="session-expires-at"]');
    var timeoutMs = getTimeoutMs();
    var newExpiryMs = null;

    if (timeoutHeader) {
      var timeoutSec = parseInt(timeoutHeader, 10);
      if (!isNaN(timeoutSec) && timeoutSec > 0) {
        localStorage.setItem(STORAGE_TIMEOUT_KEY, String(timeoutSec * 1000));
        timeoutMs = timeoutSec * 1000;
      }
    }

    if (expiryHeader) {
      var expiresMs = parseInt(expiryHeader, 10) * 1000;
      if (!isNaN(expiresMs)) {
        if (meta) meta.setAttribute('content', expiryHeader);
        newExpiryMs = expiresMs;
      }
    }

    if (!newExpiryMs) {
      newExpiryMs = Date.now() + timeoutMs;
    }

    localStorage.setItem(STORAGE_EXPIRY_KEY, String(newExpiryMs));
  }

  function createModal() {
    if (document.getElementById('session-timeout-modal')) return;

    var modal = document.createElement('div');
    modal.id = 'session-timeout-modal';
    modal.className = 'fixed inset-0 z-50 hidden overflow-y-auto';
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');

    modal.innerHTML = [
      '<div class="flex min-h-full items-center justify-center p-4 text-center sm:p-0">',
        '<div data-modal-dialog class="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg opacity-0 scale-95">',
          '<div class="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">',
            '<div class="sm:flex sm:items-start">',
              '<div class="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">',
                '<svg class="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" aria-hidden="true">',
                  '<path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />',
                '</svg>',
              '</div>',
              '<div class="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left w-full">',
                '<h3 class="text-base font-semibold leading-6 text-gray-900" id="modal-title">Session Expiring</h3>',
                '<div class="mt-2">',
                  '<p class="text-sm text-gray-500">Your session will expire in <span id="session-timeout-countdown" class="font-bold text-red-600">--</span> seconds due to inactivity. Click continue to stay logged in.</p>',
                '</div>',
              '</div>',
            '</div>',
          '</div>',
          '<div class="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">',
            '<button type="button" id="session-timeout-continue" class="inline-flex w-full justify-center rounded-md bg-meclinic-teal px-3 py-2 text-sm font-semibold text-white shadow-sm hover:opacity-90 sm:ml-3 sm:w-auto">Continue Session</button>',
            '<a href="/users/sign_out" data-method="delete" class="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto">Sign Out</a>',
          '</div>',
        '</div>',
      '</div>'
    ].join('');

    document.body.appendChild(modal);

    document.getElementById('session-timeout-continue').addEventListener('click', function(e) {
      e.preventDefault();
      extendSession();
    });
  }

  function updateCountdown(seconds) {
    var el = document.getElementById('session-timeout-countdown');
    if (el) el.textContent = seconds;
  }

  function isModalVisible() {
    var modal = document.getElementById('session-timeout-modal');
    return modal && !modal.classList.contains('hidden');
  }

  function clearCountdown() {
    if (countdownInterval) {
      clearInterval(countdownInterval);
      countdownInterval = null;
    }
  }

  function startCountdown(remaining) {
    currentRemainingMs = remaining * 1000;
    updateCountdown(remaining);
    if (countdownInterval) clearInterval(countdownInterval);
    countdownInterval = setInterval(function() {
      remaining -= 1;
      currentRemainingMs = remaining * 1000;
      updateCountdown(remaining);
      if (remaining <= 0) {
        clearInterval(countdownInterval);
        countdownInterval = null;
      }
    }, 1000);
  }

  function showWarning(actualRemainingSec) {
    createModal();
    var modal = document.getElementById('session-timeout-modal');
    if (window.ModalManager) {
      window.ModalManager.open('session-timeout-modal');
    } else if (modal) {
      modal.classList.remove('hidden');
    }

    var remaining = actualRemainingSec || Math.floor(getWarningThresholdMs() / 1000);
    startCountdown(remaining);
  }

  function hideWarning() {
    if (countdownInterval) {
      clearInterval(countdownInterval);
      countdownInterval = null;
    }
    if (window.ModalManager) {
      window.ModalManager.close('session-timeout-modal');
    } else {
      var modal = document.getElementById('session-timeout-modal');
      if (modal) modal.classList.add('hidden');
    }
  }

  /**
   * Send a ping request to the server.  Callers must set isExtending
   * before invoking if they need the guard.
   */
  function sendPing(options) {
    options = options || {};
    var csrfToken = document.querySelector('meta[name="csrf-token"]');
    if (!csrfToken) {
      if (options.onError) options.onError();
      isExtending = false;
      return;
    }
    var xhr = new XMLHttpRequest();
    xhr.open('GET', '/session/ping', true);
    xhr.setRequestHeader('Accept', 'application/json');
    xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
    xhr.setRequestHeader('X-CSRF-Token', csrfToken.getAttribute('content'));

    xhr.onreadystatechange = function() {
      if (xhr.readyState === 4) {
        if (xhr.status === 200) {
          var expiresAt = xhr.getResponseHeader('X-Session-Expires-At');
          var timeoutSec = xhr.getResponseHeader('X-Session-Timeout-Seconds');
          setExpiryFromHeader(expiresAt, timeoutSec);
          lastServerPing = Date.now();
        } else if (xhr.status === 401) {
          window.location.href = getSignInPath();
          return;
        }
        if (options.onSuccess) options.onSuccess();
        isExtending = false;
      }
    };

    xhr.onerror = function() {
      if (options.onError) options.onError();
      isExtending = false;
    };

    xhr.send();
  }

  function extendSession() {
    if (isExtending) return;
    isExtending = true;
    hideWarning();
    clearTimers();
    sendPing({
      onSuccess: function() { resetTimers(); },
      onError: function() { resetTimers(); }
    });
  }

  /**
   * Ping the server to extend the session WITHOUT hiding the warning
   * modal or clearing other timers. Used when activity is detected
   * while the warning modal is visible.
   */
  function pingWhileWarning() {
    if (isExtending) return;
    isExtending = true;
    sendPing();
  }

  function restartResumeTimer() {
    if (resumeTimer) clearTimeout(resumeTimer);
    resumeTimer = setTimeout(resumeAfterPause, RESUME_COUNTDOWN_IN_SECONDS * 1000);
  }

  function resumeAfterPause() {
    isPaused = false;
    resumeTimer = null;

    if (pausedRemainingMs <= 0) {
      signOut();
      return;
    }

    var remainingSec = Math.ceil(pausedRemainingMs / 1000);
    startCountdown(remainingSec);
    expiryTimer = setTimeout(signOut, pausedRemainingMs);
  }

  function signOut() {
    hideWarning();
    clearTimers();

    var csrfToken = document.querySelector('meta[name="csrf-token"]');
    var xhr = new XMLHttpRequest();
    xhr.open('DELETE', '/users/sign_out', true);
    xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
    if (csrfToken) {
      xhr.setRequestHeader('X-CSRF-Token', csrfToken.getAttribute('content'));
    }

    var signInPath = getSignInPath();
    xhr.onreadystatechange = function() {
      if (xhr.readyState === 4) {
        window.location.href = signInPath;
      }
    };
    xhr.onerror = function() {
      window.location.href = signInPath;
    };
    xhr.send();
  }

  function clearTimers() {
    if (expiryTimer) {
      clearTimeout(expiryTimer);
      expiryTimer = null;
    }
    if (warningTimer) {
      clearTimeout(warningTimer);
      warningTimer = null;
    }
    if (countdownInterval) {
      clearInterval(countdownInterval);
      countdownInterval = null;
    }
    if (activityDebounce) {
      clearTimeout(activityDebounce);
      activityDebounce = null;
    }
    if (resumeTimer) {
      clearTimeout(resumeTimer);
      resumeTimer = null;
    }
    isPaused = false;
    pausedRemainingMs = 0;
    currentRemainingMs = 0;
  }

  function resetTimersFromExpiry(expiresAt) {
    clearTimers();

    if (!expiresAt) return;

    var now = Date.now();
    var timeUntilExpiry = expiresAt - now;

    if (timeUntilExpiry <= 0) {
      signOut();
      return;
    }

    var warningThresholdMs = getWarningThresholdMs();
    if (timeUntilExpiry <= warningThresholdMs) {
      // Show warning immediately with actual remaining time
      showWarning(Math.max(1, Math.floor(timeUntilExpiry / 1000)));
    } else {
      var timeUntilWarning = timeUntilExpiry - warningThresholdMs;
      warningTimer = setTimeout(function() {
        showWarning(Math.max(1, Math.floor(getWarningThresholdMs() / 1000)));
      }, timeUntilWarning);
    }
    expiryTimer = setTimeout(signOut, timeUntilExpiry);
  }

  function resetTimers() {
    resetTimersFromExpiry(getExpiryTimestamp());
  }

  /**
   * On any user activity (mouse, keyboard, scroll, touch), reset the
   * local timers so the warning modal doesn't appear while the user is
   * actively working.
   *
   * When the warning modal is visible, activity pauses the countdown
   * and pings the server to extend the session. If the user stops
   * being active for 15 seconds, the countdown resumes from the
   * original paused value (not the newly refreshed server timeout).
   *
   * When the modal is not visible, activity resets the local timers.
   * We also send a lightweight /session/ping once every two-thirds of
   * the timeout interval to keep the server-side Devise :timeoutable
   * session in sync.
   */
  function onUserActivity() {
    if (!isLoggedIn()) return;
    if (isExtending) return;

    if (isModalVisible()) {
      if (!isPaused) {
        isPaused = true;
        pausedRemainingMs = currentRemainingMs;
        clearCountdown();
        if (expiryTimer) {
          clearTimeout(expiryTimer);
          expiryTimer = null;
        }
        pingWhileWarning();
      }
      restartResumeTimer();
      if (Date.now() - lastServerPing > Math.floor(getTimeoutMs() * 2 / 3)) {
        pingWhileWarning();
      }
      return;
    }

    var timeoutMs = getTimeoutMs();
    var newExpiry = Date.now() + timeoutMs;

    var needsServerPing = Date.now() - lastServerPing > Math.floor(timeoutMs * 2 / 3);
    if (needsServerPing) {
      extendSession();
      return;
    }

    resetTimersFromExpiry(newExpiry);

    if (activityDebounce) clearTimeout(activityDebounce);
    activityDebounce = setTimeout(function() {
      localStorage.setItem(STORAGE_EXPIRY_KEY, String(newExpiry));
    }, ACTIVITY_DEBOUNCE_MS);
  }

  function throttle(fn, ms) {
    var last = 0;
    return function() {
      var now = Date.now();
      if (now - last < ms) return;
      last = now;
      fn();
    };
  }

  function init() {
    if (!isLoggedIn()) return;

    resetTimers();

    document.addEventListener('turbolinks:load', function() {
      resetTimers();
    });

    window.addEventListener('storage', function(e) {
      if (e.key === STORAGE_EXPIRY_KEY) {
        resetTimers();
      }
    });

    // Global activity listeners — reset the countdown while the user is active.
    var throttledActivity = throttle(onUserActivity, MOUSEMOVE_THROTTLE_MS);
    document.addEventListener('mousemove', throttledActivity);
    document.addEventListener('keydown', throttledActivity);
    document.addEventListener('scroll', throttledActivity);
    document.addEventListener('touchstart', throttledActivity);
  }

  init();
})();
